import { buildInvoiceNumber, type CompanyProfile } from '@/lib/companyProfile';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { ensureNumberingConfig, reserveEntityNumber } from '@/lib/coreSystem';
import { DOC_META, buildFallbackNumber, type DocType } from '@/lib/invoiceDoc';
import type { FinanceInvoice } from '@/lib/finance';

// ─────────────────────────────────────────────────────────────
// Numérotation unifiée des factures et des devis.
// Les factures utilisent la séquence légale continue du socle
// (jamais de trou), les devis ont leur propre séquence.
// ─────────────────────────────────────────────────────────────

export interface ReserveDocNumberOptions {
  docType: DocType;
  merchantId: number | null;
  profile: CompanyProfile;
  invoices: FinanceInvoice[];
  invoicePrefix?: string;
  reserveInvoiceNumber: (
    build: (p: CompanyProfile, seq: number) => string,
  ) => Promise<{ number: string }>;
}

export async function reserveDocNumber(opts: ReserveDocNumberOptions): Promise<string> {
  const { docType, merchantId, profile, invoices, invoicePrefix, reserveInvoiceNumber } = opts;
  const sameKind = invoices.filter((i) => (i.doc_type || 'invoice') === docType);
  const isQuote = docType === 'quote';
  const entityType = isQuote ? 'quote' : 'invoice';
  const prefix = isQuote ? DOC_META.quote.prefix : invoicePrefix || DOC_META.invoice.prefix;

  try {
    if (merchantId) {
      const org = await getOrganizationByCommerce(merchantId);
      if (org) {
        await ensureNumberingConfig({
          tenantId: org.tenant_id,
          organizationId: org.id,
          entityType,
          prefix,
          padding: 5,
          useYear: true,
        });
        const reserved = await reserveEntityNumber(org.id, entityType);
        if (reserved) return reserved;
      }
    }
  } catch {
    /* repli ci-dessous */
  }

  if (!isQuote) {
    try {
      const fallback = await reserveInvoiceNumber(buildInvoiceNumber);
      return fallback.number;
    } catch {
      /* repli ci-dessous */
    }
  }

  return buildFallbackNumber(docType, sameKind.length + 1);
}